import {
  DefaultFetchError,
  FetchCreate,
  FetchDelete,
  FetchGet,
  FetchGetId,
  FetchSuccess,
  FetchUpdate,
  PagingDataResponse,
  PagingParams,
  useFetchCreate,
  useFetchDelete,
  useFetchGet,
  useFetchGetId,
  useFetchUpdate,
} from "../fetch";

export type RecordingType = "video" | "audio";

export interface RecordingVaad {
  id: string;
  name: string;
}

export interface RecordingMeeting {
  id: string;
  name: string;
}

export interface Recording {
  id: string;
  name: string;
  type: RecordingType;
  fileLink: string;
  vaad: RecordingVaad | null;
  meeting: RecordingMeeting | null;
  createdAt: string;
  updatedAt: string;
  globalContent?: boolean;
  middahs: { id: string; name: string }[];
}

export interface RecordingTableRow extends Recording {
  key: string;
}

export interface RecordingTableData {
  data: RecordingTableRow[];
  total: number;
}

export interface RecordingCreateParams {
  name: string;
  type: RecordingType;
  fileLink: string;
  vaadId?: string;
  meetingId?: string;
}

export interface RecordingUpdateParams {
  name?: string;
  type?: RecordingType;
  fileLink?: string;
  vaadId?: string;
  meetingId?: string;
}

export interface RecordingGetParams
  extends PagingParams, Partial<Omit<Recording, "id" | "vaad" | "meeting">> {
  orderByColumn?: keyof Omit<Recording, "id" | "vaad" | "meeting">;
  type?: RecordingType;
  vaadId?: string;
  meetingId?: string;
}

export const useRecordingGet = <DD = PagingDataResponse<Recording>>(
  decorateData?: (data: PagingDataResponse<Recording>) => DD,
): FetchGet<
  PagingDataResponse<Recording>,
  RecordingGetParams,
  DefaultFetchError,
  DD
> =>
  useFetchGet("recordings", {
    autoStart: false,
    startStateLoading: false,
    decorateData,
  });

export const useRecordingTableGet = () =>
  useRecordingGet<RecordingTableData>(
    ({ data, meta }: PagingDataResponse<Recording>): RecordingTableData => ({
      data: data.map((item) => ({
        key: item.id,
        ...item,
      })),
      total: meta.itemCount,
    }),
  );

export interface RecordingVaadGetParams extends PagingParams {
  search?: string;
}

export interface RecordingVaadTableData {
  data: RecordingVaad[];
  total: number;
}

export const useRecordingVaadGet = <DD = PagingDataResponse<RecordingVaad>>(
  decorateData?: (data: PagingDataResponse<RecordingVaad>) => DD,
): FetchGet<
  PagingDataResponse<RecordingVaad>,
  RecordingVaadGetParams,
  DefaultFetchError,
  DD
> =>
  useFetchGet("recordings/vaads", {
    autoStart: false,
    startStateLoading: false,
    decorateData,
  });

export const useRecordingVaadTableGet = () =>
  useRecordingVaadGet<RecordingVaadTableData>(
    ({ data, meta }: PagingDataResponse<RecordingVaad>): RecordingVaadTableData => ({
      data: data || [],
      total: meta?.itemCount ?? 0,
    }),
  );

export const useRecordingGetById = <DD = Recording>(
  id?: string,
  decorateData?: (data: Recording) => DD,
): FetchGetId<Recording, DefaultFetchError, unknown, DD> =>
  useFetchGetId("recordings", id, {
    autoStart: !!id,
    decorateData,
  });

export const useRecordingCreate = (): FetchCreate<
  RecordingCreateParams,
  DefaultFetchError,
  RecordingCreateParams
> => useFetchCreate("recordings");

export const useRecordingUpdate = (
  id?: string,
): FetchUpdate<
  RecordingUpdateParams,
  DefaultFetchError,
  RecordingUpdateParams
> => useFetchUpdate("recordings", id);

export const useRecordingDelete = (
  id?: string,
): FetchDelete<FetchSuccess, DefaultFetchError> =>
  useFetchDelete("recordings", id);
